import styled from '@emotion/styled'
import { useWizardNavigation } from './WizardContext'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'

type Props = {
  primaryLabel: string
  onPrimaryClick: () => void
  onSkipClick: () => void
  /** Called when there's no step to go back to and the user clicks Cancel. */
  onCancelClick: () => void
  isPrimaryDisabled?: boolean
}

const WizardFooter = ({
  primaryLabel,
  onPrimaryClick,
  onSkipClick,
  onCancelClick,
  isPrimaryDisabled,
}: Props) => {
  const { state, goBack } = useWizardNavigation()
  const canGoBack = state.history.length > 0
  const isSubmitDisabled =
    isPrimaryDisabled === true || state.isSubmitting === true

  const isInFlight = state.isSubmitting === true

  return (
    <Container>
      <ButtonRow>
        {canGoBack ? (
          <BackButton type='button' onClick={goBack} disabled={isInFlight}>
            <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
              BACK
            </Text>
          </BackButton>
        ) : (
          <BackButton
            type='button'
            onClick={onCancelClick}
            disabled={isInFlight}
          >
            <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
              CANCEL
            </Text>
          </BackButton>
        )}
        <NextButton
          type='button'
          onClick={onPrimaryClick}
          disabled={isSubmitDisabled}
        >
          <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
            {primaryLabel.toUpperCase()}
          </Text>
        </NextButton>
      </ButtonRow>
      {state.currentStep === 'create' ? (
        <SkipButton type='button' onClick={onSkipClick}>
          <Text variant='paragraphSmall' color={colors.PRIMARY}>
            Skip Setup Wizard
          </Text>
        </SkipButton>
      ) : null}
    </Container>
  )
}

export default WizardFooter

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 24px 16px;
  background: ${colors.SECONDARY_500};
`

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  max-width: 720px;
`

const BackButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 0;
  background: transparent;
  border: 2px solid ${colors.PRIMARY};
  border-radius: 16px;
  cursor: pointer;
  transition: background 120ms ease;
  &,
  & * {
    cursor: pointer;
  }
  &:hover {
    background: color-mix(in srgb, ${colors.PRIMARY} 8%, transparent);
  }
  &:disabled,
  &:disabled * {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:disabled:hover {
    background: transparent;
  }
`

const NextButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 0;
  background: ${colors.PRIMARY};
  border: 2px solid ${colors.PRIMARY};
  border-radius: 16px;
  cursor: pointer;
  transition: opacity 120ms ease;
  &,
  & * {
    cursor: pointer;
  }
  &:hover {
    opacity: 0.92;
  }
  &:disabled,
  &:disabled * {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SkipButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  margin-top: 16px;
  &,
  & * {
    cursor: pointer;
  }
`
